import { Component, ElementRef, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { BehaviorSubject } from 'rxjs';
import { marked } from 'marked';

import { MainFacade } from '../../main.facade';
import { MainUtilities } from '../../main.utilities';

import {
  BasicFile,
  DisplayType,
  FileFolder,
  FileProject,
  IFile,
  IFileInfo,
  ImageFile,
} from '../../models/file-info';
import { ResultsSummaryFacade } from '../results-summary/results-summary.facade';
import { SpatialOmicsService } from '../../../services/spatial-omics.service';
import { AnnotationsService } from '../../../services/annotations.service';

@Component({
  selector: 'file-viewer',
  templateUrl: './file-viewer.component.html',
  styleUrls: ['./file-viewer.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class FileViewerComponent implements OnInit {
  readonly DisplayType = DisplayType;
  //readonly srcUsersGuidePdf = './assets/How-to-Use-JAX-Image-Tools.pdf';

  selectedFile: IFileInfo | null = null;
  displayType: DisplayType = DisplayType.None;
  isLoading!: boolean;

  isGrayscale = false;
  isStack = false;

  @ViewChild('viewer') canvasElement!: ElementRef;
  viewerDimensions: BehaviorSubject<number[]> = new BehaviorSubject<number[]>([0, 0]);

  width = 0;
  height = 0;
  top = 0;
  left = 0;

  // Release notes panel — rendered from CHANGELOG.md, which is the single
  // source of truth for release history (also referenced by the repo's
  // README.md). The build copies CHANGELOG.md into /assets/ (see
  // project.json's assets array), so it's served alongside the bundled app.
  releaseNotesHtml: SafeHtml = '';
  releaseNotesError: string | null = null;

  constructor(
    private resultsSummaryFacade: ResultsSummaryFacade,
    private mainFacade: MainFacade,
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    private spatialOmics: SpatialOmicsService,
    private annotations: AnnotationsService
  ) {}

  ngOnInit(): void {
    this.loadReleaseNotes();

    this.mainFacade.getSelectedNode$().subscribe({
      next: (node) => {
        if (node == undefined || node.data == undefined) {
          this.selectedFile = null;
        } else {
          this.selectedFile = node.data;
          if (this.selectedFile != null) {
            this.displayType = this.getDisplayType(this.selectedFile);
            // Leave the modes this file is not in first, then open it: a clear takes down the
            // loading overlay of an open it drops, which must not be the overlay this file's
            // own open has just put up.
            if (this.displayType !== DisplayType.SpatialOmics) {
              // Anything else leaves spatial mode, so its plot types leave the selector.
              this.spatialOmics.clear();
            }
            if (this.displayType !== DisplayType.Annotations) {
              // Another file replaces the annotations' image: release it (and drop an open in flight).
              this.annotations.clear();
            }
            if (this.displayType === DisplayType.SpatialOmics) {
              void this.spatialOmics.openDataset(this.selectedFile);
            }
            if (this.displayType === DisplayType.Annotations) {
              void this.annotations.open(this.selectedFile);
            }
            this.mainFacade.selectDisplayType(this.displayType);
          }
        }
      },
      error: (err) => {
        console.error(`Error with node selection: ${err}`);
      },
    });

    this.mainFacade.isFileLoading$().subscribe({
      next: (isLoading) => {
        this.isLoading = isLoading;
      }
    })
  }

  /** Whether the visualizer shows: an image, a spatial-omics dataset, or annotations by themselves. */
  get showsVisualizer(): boolean {
    return (
      this.displayType === DisplayType.Diagram ||
      this.displayType === DisplayType.SpatialOmics ||
      this.displayType === DisplayType.Annotations
    );
  }

  private getDisplayType(file: IFileInfo): DisplayType {
    // A dataset file opens on a plain click; a folder only on the explicit context-menu
    // action, since telling a bundle folder apart takes a server round-trip.
    if (this.annotations.consumeRequest(file)) {
      return DisplayType.Annotations;
    }
    if (this.spatialOmics.consumeRequest(file) || SpatialOmicsService.isDatasetFile(file)) {
      return DisplayType.SpatialOmics;
    }
    if (file instanceof FileProject) {
      return DisplayType.None;
    } else if (file instanceof FileFolder) {
      if (file.results) {
        this.resultsSummaryFacade.fetchResultsSummary();
        return DisplayType.ResultsSummary;
      } else {
        return DisplayType.None;
      }
    } else if (file instanceof ImageFile) {
      return DisplayType.Diagram;
    } else if (file instanceof BasicFile) {
      const selectedFile: IFile = <IFile>file;
      const extension = selectedFile.fileExtension?.toLowerCase() ?? '';
      if (extension === 'csv') {
        this.mainFacade.fetchTableData();
        return DisplayType.TabDelimited;
      } else if (MainUtilities.BINARY_EXTENSIONS.includes(extension)) {
        return DisplayType.None;
      } else {
        // Anything else is tried as text - the code panel says so if it turns out not to be.
        this.mainFacade.fetchContent();
        return DisplayType.Code;
      }
    } else {
      console.error('Selected node is an unrecognized type');
      return DisplayType.None;
    }
  }

  setIsStack(isStack: boolean) {
    this.isStack = isStack;
  }
  setIsGrayscale(isGrayscale: boolean) {
    this.isGrayscale = isGrayscale;
  }

  /**
   * Fetches CHANGELOG.md and renders it into the release-notes panel. The
   * file lives at the repo root as the single source of truth — the build
   * pipeline copies it into /assets/CHANGELOG.md so it's reachable from the
   * deployed app. Falls back to an inline error message when the fetch
   * fails (offline dev, stale build, asset glob misconfigured).
   */
  private loadReleaseNotes(): void {
    // responseType:'text' — we want raw markdown, not Angular's default JSON parse.
    this.http.get('assets/CHANGELOG.md', { responseType: 'text' }).subscribe({
      next: (md) => {
        // marked.parse can return a Promise in async mode; the default sync
        // mode returns a string. Coerce defensively so the type checker is
        // happy in either case.
        const html = marked.parse(md, { async: false }) as string;
        // Marked output is HTML we generated from our own markdown file —
        // safe to bypass sanitisation. If CHANGELOG.md ever takes
        // user-generated input, drop bypassSecurityTrustHtml.
        this.releaseNotesHtml = this.sanitizer.bypassSecurityTrustHtml(html);
      },
      error: (err) => {
        this.releaseNotesError =
          'Unable to load release notes (CHANGELOG.md). See the project README on GitHub for release history.';
        console.error('Failed to fetch CHANGELOG.md', err);
      },
    });
  }
}
